import type { TargettedEvent } from '@suntex.core/animation';
import { LoadUtil, UIUtil } from '@suntex.core/animation';
import type { Color } from '@suntex.core/color';
import { ValueText } from '@suntex.modules/core';

import type { VifBlockGameStage } from '../types';

import type { BoardGeometry } from './BoardGeometry';
import { BoardGameEngine } from './boardGameEngine';
import { cellAt } from './grid';

/**
 * Shared base for the 4x4 "block" tile-matching games (AddSub, MulDiv).
 * Carries every genuinely block-shared member (concrete) plus the per-game
 * differences as protected overridable hooks. `VIFAddSub` and `VIFMulDiv`
 * both extend this directly, overriding the hooks with their own bodies and
 * reading/writing engine state (`this.topEQs`, etc.) as plain `this` access.
 */
export abstract class BlockBoardEngine extends BoardGameEngine<VifBlockGameStage> {
  protected get gridRows(): number {
    return 4;
  }

  protected get gridCols(): number {
    return 4;
  }

  protected tileBackFrame(): number {
    return 2;
  }

  protected onActivateAfterReset(): void {
    this.enableStartButton();
  }

  protected bottomHalfCorrectFrame(): number {
    return 3;
  }

  protected playCorrectSound(): void {
    LoadUtil.playSound('correct');
  }

  protected tEqValueAt(index: number): string | number {
    return this.tEqValues[index]!;
  }

  protected checkForEnd(): void {
    this.board.enabled = false;
    if (this.intCorrect >= 16) {
      this.runAnimation();
      LoadUtil.playSound('win');
      this.onWinBeforeGameOver();
      this.gameOver();
    } else {
      this.board.enabled = true;
    }
  }

  /** Called inside `checkForEnd`'s win branch, after the 'win' sound plays and before `gameOver()`. */
  protected onWinBeforeGameOver(): void {}

  protected tradePiece(): void {
    if (
      this.dragging &&
      this.board.enabled &&
      this.currCol != null &&
      this.currRow != null
    ) {
      const tradingText = cellAt(this.board, this.currRow, this.currCol).t
        .textb;
      const tValue = this.tileOrigAnswers[4 * this.currRow + this.currCol]!;
      const myValue =
        this.tileOrigAnswers[4 * this.startRow! + this.startCol!]!;
      this.tileOrigAnswers[4 * this.startRow! + this.startCol!] = tValue;
      this.tileOrigAnswers[4 * this.currRow + this.currCol] = myValue;
      ValueText.displayText(tradingText, myValue);
      const draggingText = this.draggingPiece!.textb;
      ValueText.displayText(draggingText, tValue);
      this.tileLocked[this.currRow]![this.currCol] = 1;
      if (myValue == this.tEqValues[4 * this.currRow + this.currCol]) {
        this.releasePiece();
        this.board.enabled = false;
        this.correctAnswer();
        this.checkForBonus(tValue);
      } else {
        this.releasePiece();
        this.board.enabled = false;
        this.incorrectAnswer();
      }
    }
  }

  protected releasePiece(): void {
    if (this.dragging) {
      UIUtil.hide(this.mobilePiece, 300);
      UIUtil.show(this.draggingPiece, 300);
      this.fadeAllTiles(1, this.animMs / 2);
      this.dragging = false;
      this.draggingPiece = null;
    }
  }

  protected initGame(): void {
    const { params } = this.args;
    this.gameFlag = params.moduleId - 166 + 1;
    this.playerId = params.playerId;
    this.vifStage = params.stage;

    this.gameFrame.roundsInModule = 4;
    this.gameFrame.gamesPerRound = 4;

    this.startSeconds = this.computeStartSeconds(this.gameFlag);
    this.onGameFlagResolved(this.gameFlag);

    this.gameFrame.disableMoreBtn = true;
    this.gameFrame.disableNextBtn = true;

    UIUtil.hide(this.gameStage);

    this.initInterface();
    this.gameFrame.initGame(
      this.startGame.bind(this),
      this.stopGame.bind(this),
    );
  }

  /* Interface */

  private initInterface() {
    UIUtil.createButton(
      this.gameStage.startButton,
      this.activateStartButton.bind(this),
      false,
      undefined,
      undefined,
    );
    this.mobilePiece = this.gameStage.mPiece;
    this.board = this.gameStage.testWindow;
  }

  private activateStartButton(e: TargettedEvent<unknown>) {
    window.startTimeOnTask();

    const btn = UIUtil.getEventTarget(e);
    UIUtil.setFrame(btn, 2);
    UIUtil.disable(btn);
    this.nextProblem();
  }

  /* -----------------------------------------------------------------------
   * Protected hooks: per-game differences, no shared default (abstract) or a
   * block-wide default that both AddSub and MulDiv can rely on.
   * ----------------------------------------------------------------------- */

  protected abstract get correctColor(): Color;
  protected abstract get hoverAlpha(): number;
  protected abstract get geometry(): BoardGeometry;
  protected abstract computeStartSeconds(gameFlag: number): number[];

  protected onGameFlagResolved(_gameFlag: number): void {}
  protected onStartGameBegin(): void {}
  protected onBeforeHideMobilePiece(): void {}

  /** Sets the op-button frame and (AddSub only) the game/round meter frames. */
  protected abstract onSetupGame(): void;

  /** The differing "which data set gets used" branch inside `nextProblem`. */
  protected abstract populateProblem(): void;

  /** The tail of `nextProblem`. Must set `board.enabled = true` itself. */
  protected abstract finalizeNextProblem(): void;

  /** Called inside `runAnimation`, right before `intLayer++`. Meter-update formulas differ. */
  protected abstract onRunAnimationMeterUpdate(): void;
}
